import React from 'react';
import './Alert.css';

/**
 * Alert component for displaying success, error, warning, and info messages
 * Based on ArbiWin design system
 *
 * @param {Object} props - Alert properties
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.variant - Alert variant: 'success', 'error', 'warning', 'info'
 * @param {string} props.title - Alert title
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {React.ReactNode} props.icon - Custom icon component
 * @param {boolean} props.showIcon - Whether to show default icon
 * @param {string} props.size - Alert size: 'small', 'medium', 'large'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} Alert component
 */
const Alert = function Alert({
  children,
  variant = 'info',
  title,
  dismissible = false,
  onDismiss,
  icon,
  showIcon = true,
  size = 'medium',
  className = '',
  testId
}) {

  const alertClasses = [
    'arbiwin-alert',
    'arbiwin-alert--' + variant,
    'arbiwin-alert--' + size,
    dismissible ? 'arbiwin-alert--dismissible' : '',
    className
  ].filter(Boolean).join(' ');

  const getDefaultIcon = function() {
    const icons = {
      success: (
        <svg className="arbiwin-alert__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      error: (
        <svg className="arbiwin-alert__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="arbiwin-alert__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      info: (
        <svg className="arbiwin-alert__icon" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      )
    };
    return icons[variant] || icons.info;
  };

  const handleDismiss = function() {
    if (onDismiss) {
      onDismiss();
    }
  };

  return (
    <div
      className={alertClasses}
      role="alert"
      data-testid={testId || 'arbiwin-alert'}
    >
      {(showIcon || icon) && (
        <div className="arbiwin-alert__icon-wrapper">
          {icon || getDefaultIcon()}
        </div>
      )}

      <div className="arbiwin-alert__content">
        {title && (
          <div className="arbiwin-alert__title">
            {title}
          </div>
        )}
        {children && (
          <div className="arbiwin-alert__message">
            {children}
          </div>
        )}
      </div>

      {dismissible && (
        <button
          className="arbiwin-alert__dismiss"
          onClick={handleDismiss}
          aria-label="Затвори известието"
          type="button"
        >
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * SuccessAlert component - Pre-configured success alert
 *
 * @param {Object} props - SuccessAlert properties
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.title - Alert title
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SuccessAlert component
 */
const SuccessAlert = function SuccessAlert({
  children,
  title = 'Успех!',
  dismissible = true,
  onDismiss,
  className = ''
}) {
  return (
    <Alert
      variant="success"
      title={title}
      dismissible={dismissible}
      onDismiss={onDismiss}
      className={'arbiwin-alert--success-preset ' + className}
    >
      {children}
    </Alert>
  );
};

/**
 * ErrorAlert component - Pre-configured error alert
 *
 * @param {Object} props - ErrorAlert properties
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.title - Alert title
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ErrorAlert component
 */
const ErrorAlert = function ErrorAlert({
  children,
  title = 'Грешка!',
  dismissible = true,
  onDismiss,
  className = ''
}) {
  return (
    <Alert
      variant="error"
      title={title}
      dismissible={dismissible}
      onDismiss={onDismiss}
      className={'arbiwin-alert--error-preset ' + className}
    >
      {children}
    </Alert>
  );
};

/**
 * WarningAlert component - Pre-configured warning alert
 *
 * @param {Object} props - WarningAlert properties
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.title - Alert title
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} WarningAlert component
 */
const WarningAlert = function WarningAlert({
  children,
  title = 'Внимание!',
  dismissible = true,
  onDismiss,
  className = ''
}) {
  return (
    <Alert
      variant="warning"
      title={title}
      dismissible={dismissible}
      onDismiss={onDismiss}
      className={'arbiwin-alert--warning-preset ' + className}
    >
      {children}
    </Alert>
  );
};

/**
 * InfoAlert component - Pre-configured info alert
 *
 * @param {Object} props - InfoAlert properties
 * @param {React.ReactNode} props.children - Alert content
 * @param {string} props.title - Alert title
 * @param {boolean} props.dismissible - Whether alert can be dismissed
 * @param {Function} props.onDismiss - Dismiss handler function
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} InfoAlert component
 */
const InfoAlert = function InfoAlert({
  children,
  title = 'Информация',
  dismissible = false,
  onDismiss,
  className = ''
}) {
  return (
    <Alert
      variant="info"
      title={title}
      dismissible={dismissible}
      onDismiss={onDismiss}
      className={'arbiwin-alert--info-preset ' + className}
    >
      {children}
    </Alert>
  );
};

Alert.displayName = 'Alert';
SuccessAlert.displayName = 'SuccessAlert';
ErrorAlert.displayName = 'ErrorAlert';
WarningAlert.displayName = 'WarningAlert';
InfoAlert.displayName = 'InfoAlert';

export default Alert;
export { SuccessAlert, ErrorAlert, WarningAlert, InfoAlert };
